// The agent pane docked to the right of every page: the open conversations and agent
// peeks as tabs, the one in front shown, the rest kept running behind it. Minimised, it
// leaves a tab per item on the right edge. See docs/Y2026-Q4/design-agent-side-pane.md.
import { type PointerEvent, useEffect, useRef, useState } from "react";

import {
  type PaneItem,
  paneTag,
  useAgentPane,
  usePaneProjectName,
} from "../state/useAgentPane";
import { useIsMobile } from "../utils/useIsMobile";
import { slotKeyName } from "../utils/agentSlotText";
import { DEFAULT_SLOT } from "../api/agentSlot";
import { AgentSlotProvider } from "../api/AgentSlotProvider";
import { useAgentConfigs } from "../api/hooks/useAgentConfigs";
import { AgentPaneConversation } from "./AgentPaneConversation";
import { AgentPeek } from "./AgentPeek";
import styles from "./AgentPane.module.css";

const WIDTH_KEY = "inrange-agents.agentPane.width";
const MIN_WIDTH = 360;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 460;

const clampWidth = (width: number) =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));

// A per-viewer convenience: unreadable storage just means the default width.
const storedWidth = (): number => {
  try {
    const stored = Number(window.localStorage.getItem(WIDTH_KEY));
    return stored ? clampWidth(stored) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
};

const storeWidth = (width: number) => {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(width));
  } catch {
    // Not remembered; the pane still resizes.
  }
};

/** A tab: its project and kind, and a dot while the agent works in a conversation. */
const PaneTab = ({
  item: c,
  active,
  docked,
}: {
  item: PaneItem;
  active: boolean;
  /** On the right edge while the pane is minimised. */
  docked: boolean;
}) => {
  const pane = useAgentPane();
  const projectName = usePaneProjectName(c.projectId);
  const working = pane.workingIn(c.key);
  const configs = useAgentConfigs().data?.configs;
  const label = [
    projectName,
    ...(c.config === DEFAULT_SLOT ? [] : [slotKeyName(c.config, configs)]),
    paneTag(c),
  ].join(" · ");
  return (
    <span
      className={`${docked ? styles.dockTab : styles.tab} ${active ? styles.active : ""}`}
    >
      <button
        type="button"
        className={styles.tabButton}
        onClick={() => pane.select(c.key)}
        title={c.tab === "conversation" ? `${label}: ${c.title}` : label}
      >
        <span
          className={`${styles.dot} ${working ? styles.working : ""}`}
          aria-label={working ? "Working" : "Idle"}
        />
        <span className={styles.tabLabel}>{label}</span>
      </button>
      {!docked && (
        <button
          type="button"
          className={styles.tabClose}
          onClick={() => pane.close(c.key)}
          aria-label={`Close ${label}`}
        >
          ×
        </button>
      )}
    </span>
  );
};

export const AgentPane = () => {
  const pane = useAgentPane();
  const isMobile = useIsMobile();
  const [width, setWidth] = useState(storedWidth);
  // The width as the pointer last set it, which the end of a drag saves.
  const dragged = useRef<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const shown = pane.items.length > 0 && !pane.minimised;

  // Fixed-position overlays (the upload progress pill) keep clear of the pane.
  useEffect(() => {
    const root = document.documentElement;
    if (shown && !isMobile) {
      root.style.setProperty("--agent-pane-width", `${width}px`);
    } else root.style.removeProperty("--agent-pane-width");
    return () => {
      root.style.removeProperty("--agent-pane-width");
    };
  }, [shown, isMobile, width]);

  if (pane.items.length === 0) return null;

  const startDrag = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragged.current = width;
    setDragging(true);
  };
  const drag = (e: PointerEvent<HTMLDivElement>) => {
    if (dragged.current === null) return;
    dragged.current = clampWidth(window.innerWidth - e.clientX);
    setWidth(dragged.current);
  };
  const endDrag = () => {
    if (dragged.current !== null) storeWidth(dragged.current);
    dragged.current = null;
    setDragging(false);
  };

  return (
    <>
      {/* Minimised, the conversations stay mounted behind the dock, so a turn in
          flight keeps reporting and unsent text survives. */}
      <aside
        className={styles.pane}
        style={isMobile ? undefined : { width }}
        aria-label="Agent conversation"
        hidden={pane.minimised}
      >
        {!isMobile && (
          <div
            className={`${styles.resize} ${dragging ? styles.dragging : ""}`}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the agent pane"
            onPointerDown={startDrag}
            onPointerMove={drag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onLostPointerCapture={endDrag}
          />
        )}
        {pane.items.length > 1 && (
          <div className={styles.tabs}>
            {pane.items.map((c) => (
              <PaneTab
                key={c.key}
                item={c}
                active={c.key === pane.activeKey}
                docked={false}
              />
            ))}
          </div>
        )}
        {/* Above the routes, so each tab is put in its own slot here. */}
        {pane.items.map((c) => (
          <AgentSlotProvider key={c.key} projectId={c.projectId} slot={c.config}>
            {c.tab === "conversation" ? (
              <AgentPaneConversation conversation={c} hidden={c.key !== pane.activeKey} />
            ) : (
              <AgentPeek peek={c} hidden={c.key !== pane.activeKey} />
            )}
          </AgentSlotProvider>
        ))}
      </aside>
      {pane.minimised && (
        <div className={styles.dock} aria-label="Minimised agent pane tabs">
          {pane.items.map((c) => (
            <PaneTab key={c.key} item={c} active={false} docked />
          ))}
        </div>
      )}
    </>
  );
};
